// Requêtes des écrans de l'urbanisme (lot L5-C2, M18, TanStack Query), clés sous « urbanisme ». Types de
// packages/contrats/src/urbanisme.ts ; le serveur refait tous les contrôles (autorité, avis, inspection, zone).
import type {
  AutoriteUrbanisme,
  CreationAutoriteUrbanisme,
  CreationPlanUrbanisme,
  CreationTypeDemande,
  CreationZoneUrbanisme,
  DemandeUrbanisme,
  InspectionDemande,
  LocalisationDemande,
  ModificationEtatPlan,
  PlanUrbanisme,
  RenduAvis,
  ResumeDemandeUrbanisme,
  SollicitationAvis,
  TypeDemandeUrbanisme,
  UrbanismePublic,
  ZoneUrbanisme,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { requete } from '../../api/client';

export interface Liste<T> {
  elements: T[];
}

export type ProprietesZoneCarte = { objet: 'zone_urbanisme'; id: string; code: string; libelle: string; vocation: string; plan: string; plan_etat: string; centre: { longitude: number; latitude: number } };
export type ProprietesDemandeCarte = { objet: 'demande_urbanisme'; id: string; numero: string | null; type: string; avancement: string; zone: string | null };
export type CarteUrbanisme = FeatureCollection<Geometry, ProprietesZoneCarte | ProprietesDemandeCarte> & { features: Feature<Geometry, ProprietesZoneCarte | ProprietesDemandeCarte>[] };

export const INVALIDER_URBANISME: readonly (readonly unknown[])[] = [['urbanisme'], ['indicateurs']];

const lire = <T,>(chemin: string) => ({ signal }: { signal: AbortSignal }) => requete<T>(chemin, { signal });
const envoyer = <T,>(methode: 'POST' | 'PUT', chemin: string, corps?: unknown) => requete<T>(chemin, { methode, corps });

export const useDemandes = () => useQuery({ queryKey: ['urbanisme', 'demandes'], queryFn: lire<Liste<ResumeDemandeUrbanisme>>('/urbanisme/demandes'), retry: false });
export const useDemande = (id: string | undefined) => useQuery({ queryKey: ['urbanisme', 'demande', id ?? ''], queryFn: lire<DemandeUrbanisme>(`/urbanisme/demandes/${id}`), enabled: Boolean(id), retry: false });
export const useTypes = () => useQuery({ queryKey: ['urbanisme', 'types'], queryFn: lire<Liste<TypeDemandeUrbanisme>>('/urbanisme/types'), retry: false });
export const useAutorites = () => useQuery({ queryKey: ['urbanisme', 'autorites'], queryFn: lire<Liste<AutoriteUrbanisme>>('/urbanisme/autorites'), retry: false });
export const usePlans = () => useQuery({ queryKey: ['urbanisme', 'plans'], queryFn: lire<Liste<PlanUrbanisme>>('/urbanisme/plans'), retry: false });
export const useCarteUrbanisme = (bbox: string | null) =>
  useQuery({ queryKey: ['urbanisme', 'carte', bbox ?? ''], queryFn: ({ signal }) => requete<CarteUrbanisme>('/urbanisme/carte', { signal, parametres: { bbox } }), enabled: Boolean(bbox), retry: false });
export const useUrbanismePublic = (code: string | null) =>
  useQuery({ queryKey: ['public', 'urbanisme', code ?? ''], queryFn: ({ signal }) => requete<UrbanismePublic>(`/public/collectivites/${encodeURIComponent(code as string)}/urbanisme`, { signal }), enabled: Boolean(code), staleTime: 60_000 });

export const creerAutorite = (c: CreationAutoriteUrbanisme) => envoyer<AutoriteUrbanisme>('POST', '/urbanisme/autorites', c);
export const creerType = (c: CreationTypeDemande) => envoyer<TypeDemandeUrbanisme>('POST', '/urbanisme/types', c);
export const activerType = (id: string, actif: boolean) => envoyer<TypeDemandeUrbanisme>('PUT', `/urbanisme/types/${id}/activation`, { actif });
export const creerPlan = (c: CreationPlanUrbanisme) => envoyer<PlanUrbanisme>('POST', '/urbanisme/plans', c);
export const changerEtatPlan = (id: string, c: ModificationEtatPlan) => envoyer<PlanUrbanisme>('PUT', `/urbanisme/plans/${id}/etat`, c);
export const creerZone = (c: CreationZoneUrbanisme) => envoyer<ZoneUrbanisme>('POST', '/urbanisme/zones', c);
export const localiser = (id: string, c: LocalisationDemande) => envoyer<DemandeUrbanisme>('PUT', `/urbanisme/demandes/${id}/localisation`, c);
export const solliciterAvis = (id: string, c: SollicitationAvis) => envoyer<DemandeUrbanisme>('POST', `/urbanisme/demandes/${id}/avis`, c);
export const rendreAvis = (id: string, avisId: string, c: RenduAvis) => envoyer<DemandeUrbanisme>('POST', `/urbanisme/demandes/${id}/avis/${avisId}/rendu`, c);
export const lierInspection = (id: string, c: InspectionDemande) => envoyer<DemandeUrbanisme>('POST', `/urbanisme/demandes/${id}/inspections`, c);
